<template>
  <div id="accordionSetupQuotes" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingSetupQuotes" class="accordion-header">
        <button
          aria-controls="collapseSetupQuotes"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseSetupQuotes"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="description" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.quotes") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseSetupQuotes"
        aria-labelledby="headingSetupQuotes"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionSetupQuotes"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <div class="quotes-media-container d-flex flex-column">
            <v-uploader
              ref="uploader"
              :after-upload="onChange"
              route-prefix="api/drive"
              :headers="{ 'X-case': tour.clientTourId }"
              @removed="onChange"
            />

            <v-button
              :disabled="disableButton || mediaUuids.length === 0"
              class="mt-12 align-self-end"
              size="xs"
              variant="primary"
              @click="confirmMedia"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>

            <div class="media-container item-list mt-12 text-xs">
              <div class="table-responsive d-none d-md-flex d-lg-none d-xl-block">
                <v-table class="mb-0">
                  <v-table-header>
                    <tr>
                      <th class="text-xs">
                        {{ $t("general.shared.name") }}
                      </th>

                      <th class="text-xs">
                        {{ $t("general.shared.date") }}
                      </th>

                      <th class="text-xs">
                        {{ $t("general.shared.size") }}
                      </th>
                      <th class="fit" />
                    </tr>
                  </v-table-header>

                  <v-table-body class="fw-medium">
                    <tr v-if="!quotesLoaded">
                      <td class="position-relative border-0 py-96" colspan="2222">
                        <v-loader />
                      </td>
                    </tr>

                    <template v-else-if="!!quotes && quotes.length > 0">
                      <tr v-for="file in quotes" :key="file.id">
                        <td class="d-flex align-items-center">
                          <v-icon v-if="file.type === 'pdf'" class="invoice-icon" icon="pdf" space="me-8" />

                          <v-icon
                            v-else-if="file.type !== 'pdf' && file.type !== 'image'"
                            class="invoice-icon"
                            icon="doc"
                            space="me-8"
                          />

                          <v-picture v-else class="image-container">
                            <img :alt="file.storageUrl" :src="file.storageUrl" />
                          </v-picture>

                          <the-case-setup-invoices-update-name
                            :agency="agency"
                            :client-tour="clientTour"
                            :file="file"
                            :tour="tour.id"
                            @reload-tour="reloadTour"
                          />
                        </td>

                        <td class="text-nowrap">
                          {{ file.createdAt }}
                        </td>

                        <td class="text-nowrap">
                          {{ file.humanReadableSize }}
                        </td>

                        <td class="fit text-end">
                          <div class="d-flex align-items-center">
                            <v-button
                              class="btn-show-details shadow-none p-4"
                              variant="icon"
                              @click="downloadMedia(file.id)"
                            >
                              <v-icon icon="download" size="xs" />
                            </v-button>

                            <v-button
                              class="btn-show-details shadow-none p-4"
                              variant="icon"
                              @click="removeMedia(file)"
                            >
                              <v-icon icon="trash" size="xs" class="text-danger" />
                            </v-button>
                          </div>
                        </td>
                      </tr>
                    </template>

                    <template v-else>
                      <tr>
                        <td class="rounded-xs py-16 text-center text-muted italic" colspan="2222">
                          {{ $t("clientTour.invoice.noInvoicesReceived") }}
                        </td>
                      </tr>
                    </template>
                  </v-table-body>
                </v-table>
              </div>

              <div class="item-cards d-block d-md-none d-lg-block d-xl-none">
                <div v-if="!quotesLoaded" class="position-relative py-96">
                  <v-loader />
                </div>

                <template v-else-if="!!quotes && quotes.length > 0">
                  <div v-for="file in quotes" :key="file.id" class="item-card border rounded-xs p-8 mb-8">
                    <the-case-setup-invoices-update-name
                      :agency="agency"
                      :client-tour="clientTour"
                      :file="file"
                      :tour="tour.id"
                      @reload-tour="reloadTour"
                    />

                    <div>
                      <span class="fw-medium">{{ $t("general.shared.date") }}: </span>

                      <span>
                        {{ file.createdAt }}
                      </span>
                    </div>

                    <div>
                      <span class="fw-medium">{{ $t("general.shared.size") }}: </span>
                      <span>{{ file.humanReadableSize }}</span>
                    </div>

                    <div class="d-flex justify-content-end align-items-center">
                      <v-button class="btn-show-details shadow-none p-4" variant="icon" @click="downloadMedia(file.id)">
                        <v-icon icon="download" size="xs" />
                      </v-button>

                      <v-button class="btn-show-details shadow-none p-4" variant="icon" @click="removeMedia(file)">
                        <v-icon icon="trash" size="xs" class="text-danger" />
                      </v-button>
                    </div>
                  </div>
                </template>

                <div v-else class="text-center text-muted italic">
                  {{ $t("clientTour.noQuotes") }}
                </div>
              </div>
            </div>

            <div class="quote-history-section mt-16">
              <div class="d-flex align-items-center justify-content-between mb-8">
                <div class="fw-bold ff-secondary">
                  Quote History
                  <span v-if="quoteHistoryData" class="badge bg-secondary ms-4">
                    {{ quoteHistoryData.total_quotes_sent }} sent
                  </span>
                </div>
                <v-button size="xs" variant="primary" :disabled="historyLoading" @click="loadQuoteHistory">
                  <v-icon icon="refresh" size="xs" space="me-4" />
                  Refresh
                </v-button>
              </div>

              <v-loader v-if="historyLoading" />

              <table v-else-if="quoteHistoryData?.quotes?.length" class="table table-sm quote-history-table">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Source</th>
                    <th>Pax Count</th>
                    <th>Sent At</th>
                    <th>Sent By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="q in quoteHistoryData.quotes" :key="q.id">
                    <td>#{{ q.batch_id }}</td>
                    <td>
                      <span :class="q.source === 'automatic' ? 'badge bg-info' : 'badge bg-primary'">
                        {{ q.source }}
                      </span>
                    </td>
                    <td>{{ q.pax_count }}</td>
                    <td>{{ q.sent_at }}</td>
                    <td>{{ q.sent_by || 'System' }}</td>
                  </tr>
                </tbody>
              </table>

              <p v-else class="text-muted italic text-center py-8">
                No quotes have been sent yet.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import VUploader from "@/components/vendor/upload/VUploader.vue";
import Media from "@/api/objects/Media";
import Tour from "@/api/objects/Tour";
import { useUserStore } from "@/stores/user";
import VTable from "@/components/vendor/basic/table/VTable.vue";
import VTableHeader from "@/components/vendor/basic/table/VTableHeader.vue";
import VTableBody from "@/components/vendor/basic/table/VTableBody.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VPicture from "@/components/vendor/basic/picture/VPicture.vue";
import TheCaseSetupInvoicesUpdateName from "@/components/case/parts/TheCaseSetupInvoicesUpdateName.vue";
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  agency: string;
  tour: Tour;
  clientTour: number;
}>();

const userStore = useUserStore();
const toast = useToast();
const { t } = useI18n();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;

const media = ref<any[]>([]);
const disableButton = ref<boolean>(false);
const quotes = ref<Media[]>([]);
const quotesLoaded = ref<boolean>(false);
const uploader = ref<any>(null);

const quoteHistoryData = ref<any>(null);
const historyLoading = ref<boolean>(false);

const mediaUuids = computed(() =>
  media.value.map((mediaItem: any) => ({
    uuid: mediaItem.attributes.uuid,
    name: mediaItem.attributes.name,
  }))
);

onMounted(async () => {
  await loadTourQuotes();
  await loadQuoteHistory();
});

const loadQuoteHistory = async () => {
  historyLoading.value = true;
  try {
    const res = await api.case.quoteHistory(props.agency, props.tour.id);
    quoteHistoryData.value = res.content;
  } catch (e: any) {
    console.error("Failed to load quote history", e);
  } finally {
    historyLoading.value = false;
  }
};

const onChange = () => {
  if (uploader.value) {
    media.value = uploader.value.mediaLibrary.state.media;
  }
};

const loadTourQuotes = async () => {
  quotesLoaded.value = false;
  quotes.value = props.tour.quotes;
  quotesLoaded.value = true;
};

const downloadMedia = async (fileId: number) => {
  try {
    const response = await api.media.download("drive", fileId);
    window.open(response, "_blank");
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || "Download failed.");
  }
};

const removeMedia = async (file: Media) => {
  await $modal?.delete({
    title: t("clientTour.invoice.removeTitle"),
    text: t("clientTour.invoice.removeText", { name: file.name }),
    deleteButtonText: t("general.button.remove"),
    deleteButtonLoadingText: t("general.button.removing"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doRemoveMedia(file.id),
  });
};

const doRemoveMedia = async (fileId: number | string) => {
  try {
    await api.media.delete("drive", fileId);

    toast.success(t("general.shared.savedChanges"));

    await reloadTour();
    await loadTourQuotes();
  } catch (e: any) {
    console.error(e.response?.data);
    toast.error(e.response?.data?.message || "Error deleting media");
  }
};

const confirmMedia = async () => {
  try {
    disableButton.value = true;

    const data = {
      media: mediaUuids.value,
      model_type: "tour",
      model_id: props.tour.id,
      collection: "quotes",
    };

    await api.media.confirm("drive", data);

    toast.success(t("general.shared.savedChanges"));

    // Clear uploaded media
    media.value = [];
    if (uploader.value) {
      uploader.value.mediaLibrary.state.media = [];
    }

    await reloadTour();
    await loadTourQuotes();
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message || "Error confirming media");
  } finally {
    disableButton.value = false;
  }
};

const reloadTour = async () => {
  const tourData = {
    id: props.tour.id,
    agency: props.agency,
    clientTourId: props.clientTour,
  };

  const params = {
    expand: ["client", "paxs", "featuredImage", "quotes", "invoicesReceived", "clientTour"],
  };

  await userStore.setCurrentTour(tourData, params);
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

.media-container {
  th {
    font-size: 0.75rem;
  }

  .invoice-icon {
    font-size: 1.5rem;
  }

  .image-container {
    margin-right: 0.5rem;

    :deep(img) {
      height: 100%;
      max-height: 2rem;
    }
  }
}

.quote-history-section {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;

  .quote-history-table {
    font-size: 0.8rem;

    th {
      color: #6b6b6b;
      font-weight: 600;
    }

    td {
      vertical-align: middle;
    }
  }
}
</style>
